import get from 'lodash/get'
import * as Sentry from '@sentry/react'

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { byId } from '@fielded/fs-api/lib/tools'

import { userLocationLabelFromId } from '../../../../subapps/settings/common/utils'
import { getLocationsByLevelOrder } from '../../../../common/utils'
import { updateCurrentMasterData } from '../../../../van-shared/utils/utils'

const getProcessedMasterData = ({ locations, products, programs, funders }) => {
  const locationsByLevel = getLocationsByLevelOrder(locations)

  // We generate all the search options here to not drop performance
  const locationOptions = locationsByLevel.map(locationId => {
    return { label: userLocationLabelFromId(locationId), value: locationId }
  })
  const funderOptions = funders.map(funder => { return {label: funder.name, value: funder._id} })
  const productOptions = products.map(product => { return {label: product.name, value: product._id} })

  return {
    programs: byId(programs),
    products: {
      byId: byId(products),
      allIds: products.map(pr => pr._id),
      allProducts: products,
      productOptions
    },
    locations: {
      byId: byId(locations),
      allLocations: locations,
      locationOptions
    },
    funders: {
      byId: byId(funders),
      funderOptions
    }
  }
}

const initialState = {
  hasReceivedMasterData: false,
  error: null,
  programs: {},
  products: {
    byId: {},
    allIds: [],
    allProducts: [],
    productOptions: []
  },
  locations: {
    byId: {},
    allLocations: [],
    locationOptions: []
  },
  funders: {
    byId: {},
    funderOptions: []
  }
}

export const fetchMasterData = createAsyncThunk('analytics/fetchMasterData', async (args, thunkAPI) => {
  const { extra: { api, user, locationId } } = thunkAPI
  const programs = get(user, 'programs')

  const promises = [
    api.location.listChildren(locationId, { deep: true, includeSelf: true }),
    api.product.listForPrograms(programs),
    api.program.list(),
    api.funders.list()
  ]

  try {
    const response = await Promise.all(promises)
    const [locs, products, programList, funders] = response
    if (
      (!Array.isArray(programList) || !Array.isArray(products) || !Array.isArray(funders) || !Array.isArray(locs)) &&
      // guard to run only once per page load
      !window.__analyticsMasterDataShapeLogged
    ) {
      window.__analyticsMasterDataShapeLogged = true
      Sentry.captureMessage('analytics_master_data_invalid_shape', {
        extra: {
          locationId,
          userLocationId: get(user, 'location.id'),
          locsType: typeof locs,
          productsType: typeof products,
          programListType: typeof programList,
          fundersType: typeof funders,
          isLocsArray: Array.isArray(locs),
          isProductsArray: Array.isArray(products),
          isProgramListArray: Array.isArray(programList),
          isFundersArray: Array.isArray(funders)
        }
      })
    }
    if (!Array.isArray(programList)) {
      Sentry.captureMessage('analytics_program_list_invalid_shape', {
        extra: {
          locationId,
          userLocationId: get(user, 'location.id'),
          responseType: typeof programList
        }
      })
    }
    const locations = locs.filter(item => item && !!item.configurations)
    const processedMasterData = getProcessedMasterData({ locations, products, programs: programList, funders })
    return processedMasterData
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        locationId,
        userLocationId: get(user, 'location.id')
      }
    })
    return error
  }
})

export const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterData.fulfilled, (state, action) => {
        if (Object.keys(action.payload).length) {
          const { programs, products, locations, funders } = action.payload
          state.programs = programs
          state.products = products
          state.locations = locations
          state.funders = funders
          state.hasReceivedMasterData = true
        }
      })
      .addCase(fetchMasterData.rejected, (state, action) => {
        state.error = action.payload
      })
  }
})

export const selectMasterData = (state) => {
  const newState = updateCurrentMasterData(state, 'masterDataAnalytics')
  return get(newState, 'masterData')
}

export const selectHasError = (state) => {
  const newState = updateCurrentMasterData(state, 'masterDataAnalytics')
  return get(newState, 'masterData.error')
}

export const selectHasReceivedMasterData = (state) => {
  const newState = updateCurrentMasterData(state, 'masterDataAnalytics')
  return get(newState, 'masterData.hasReceivedMasterData')
}

export default masterDataSlice.reducer
